import { useCallback, useEffect, useRef, useState } from 'react';
import { INDEXED_DB_STORES, dbDelete, dbGet, dbSet } from '../utils';

export function useIndexedDBState<T>(
  storeName: (typeof INDEXED_DB_STORES)[keyof typeof INDEXED_DB_STORES],
  key: string | null | undefined,
  defaultValue: T,
) {
  const defaultRef = useRef(defaultValue);
  const [value, setValueState] = useState<T>(defaultValue);
  const [isInitialedValue, setIsInitialedValue] = useState(false);

  /**
   * Load value from IndexedDB
   */
  useEffect(() => {
    if (!key) return;

    let cancelled = false;

    (async () => {
      const stored = await dbGet<T>(storeName, key);

      if (!cancelled) {
        setValueState(stored ?? defaultRef.current);
        setIsInitialedValue(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeName, key]);

  /**
   * Setter that also writes to IndexedDB
   */
  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (!key) return;

      setValueState(prev => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;

        dbSet(storeName, key, resolved);
        return resolved;
      });
    },
    [storeName, key],
  );

  /**
   * Remove value from IndexedDB and reset state
   */
  const remove = useCallback(async () => {
    if (!key) return;

    await dbDelete(storeName, key);
    setValueState(defaultRef.current);
  }, [storeName, key]);

  return [value, setValue, remove, isInitialedValue] as const;
}
